import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    FormFeedback,
    Input,
    Row,
} from "reactstrap";
import dayjs from "dayjs";
import * as Yup from "yup";
import { toast } from "react-toastify";
import Select from "react-select";

import CommonTable from "components/Common/TableContainer";
import Loaders from "components/Common/Loaders";
import { AsyncClientReportClientSelect } from "components/Common/AsyncSearchSelects";
import {
    applyClientAsyncFilterOptionToFormState,
    buildClientAsyncFilterInitialOption,
    getClientAsyncFilterLabelFieldKey,
    normalizeMetadataFilterDateForApi,
} from "helpers/metadataFilterHelpers";
import { phoneNumberShowFormat } from "helpers/GeneralHelper";
import {
    fetchReportMailSourceOptions,
    mapReportMailSourceOptions,
} from "model/InProgressReportModel";
import {
    fetchClientReportReportTypeOptions,
    fetchClientReportSubmitActionOptions,
    submitClientReportFilterData,
    submitClientReportSendData,
} from "model/ClientReportModel";

const selectMenuStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
};

const TABLE_COLUMNS = [
    { Header: "Name", accessor: "name" },
    { Header: "Fedex ID", accessor: "fedex_id" },
    { Header: "Phone", accessor: "phone" },
    { Header: "Email", accessor: "email" },
    { Header: "Position", accessor: "position" },
    { Header: "Drug Test", accessor: "status" },
    { Header: "Existing MEC", accessor: "vLink" },
    { Header: "CV", accessor: "video" },
    { Header: "Notes", accessor: "notes" },
    { Header: "Ready To Send?", accessor: "ready_to_send" },
];

const FEDEX_SECTION_KEYS = new Set([
    "fedexdriver",
    "fedex-driver",
    "fedexdriveritems",
    "fedex",
]);

const normalizeSectionKey = (value = "") =>
    String(value ?? "")
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, "");

const formatDrugTestLabel = (value = "") => {
    const raw = String(value ?? "").trim();
    if (!raw) return "";
    return raw
        .split(/\s+/)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(" ");
};

const formatReadyToSendStatusLabel = (value = "") => {
    const raw = String(value ?? "").trim();
    if (!raw) return "";
    return raw
        .split(/\s+/)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(" ");
};

const strOrNull = (value) => {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return normalized === "" ? null : normalized;
};

const mapRowsFromApi = (rows = []) =>
    (Array.isArray(rows) ? rows : []).map((item = {}, index) => {
        const specialNotes = String(item?.specialNotes ?? item?.specialNote ?? "").trim();
        const drugTestStatus =
            item?.drugTest ??
            item?.drugTestStatus ??
            item?.drugTestResult ??
            item?.drugStatus ??
            "";
        const drugTestLabel = formatDrugTestLabel(drugTestStatus);
        const readyToSendStatus =
            item?.readyToSendStatus ??
            item?.ready_to_send_status ??
            item?.readyToSend ??
            item?.ready_to_send ??
            item?.isReadyToSend ??
            "";
        const readyToSendStatusLabel = formatReadyToSendStatusLabel(readyToSendStatus);
        const readyToSendNotes = String(item?.readyToSendNotes ?? "").trim();

        return {
            ...item,
            id: item?.candidateClientId ?? item?.id ?? item?._id ?? `row-${index}`,
            candidateClientId: item?.candidateClientId ?? "",
            name: item?.candidateFullName ?? item?.name ?? item?.candidateName ?? "",
            specialNotes,
            hasSpecialNote: Boolean(
                item?.hasSpecialNote ?? item?.isSpecialNote ?? specialNotes
            ),
            fedex_id: item?.fedexId ?? item?.fedex_id ?? item?.fedexID ?? "",
            phone: phoneNumberShowFormat(item?.candidatePhone ?? item?.phone ?? ""),
            email: item?.candidateEmail ?? item?.email ?? "",
            position: item?.jobName ?? item?.position ?? item?.jobTitle ?? "",
            orderStatus: item?.orderStatus ?? "",
            profileStatus: item?.profileStatus ?? "",
            status: drugTestLabel,
            statusLabel: drugTestLabel,
            vLink:
                item?.mecImageLink ??
                item?.existingMecUrl ??
                item?.existingMecLink ??
                item?.existing_mec ??
                item?.existingMec ??
                "",
            video: item?.cvUrl ?? item?.cvLink ?? item?.cv ?? "",
            notes: item?.notes ?? item?.note ?? "",
            readyToSendNotes,
            readyToSendStatusLabel,
            readyToSendStatusColor: item?.readyToSendStatusColor ?? "",
            ready_to_send: readyToSendStatusLabel,
        };
    });

const resolveSectionBucket = (sectionKey = "") => {
    const normalized = normalizeSectionKey(sectionKey);
    if (FEDEX_SECTION_KEYS.has(normalized)) return "fedexDriver";
    return "eligible";
};

const buildSectionsFromResponse = (nestedData = {}, responseData = {}) => {
    const apiSections = Array.isArray(nestedData?.sections)
        ? nestedData.sections
        : Array.isArray(responseData?.sections)
            ? responseData.sections
            : [];

    if (apiSections.length) {
        return apiSections
            .map((section = {}, index) => {
                const sectionKey =
                    section?.sectionKey ??
                    section?.key ??
                    section?.type ??
                    `section-${index}`;
                const rows = mapRowsFromApi(
                    section?.rows ?? section?.items ?? section?.candidates ?? []
                );
                if (!rows.length) return null;
                return {
                    sectionKey,
                    bucket: resolveSectionBucket(sectionKey),
                    sectionTitle: String(section?.sectionTitle ?? section?.title ?? "").trim(),
                    rows,
                    selectedIds: [],
                };
            })
            .filter(Boolean);
    }

    const eligibleRows = mapRowsFromApi(
        nestedData?.eligibleItems ??
            nestedData?.eligibleRows ??
            responseData?.eligibleItems ??
            responseData?.eligibleRows ??
            []
    );
    const fedexRows = mapRowsFromApi(
        nestedData?.fedexDriverItems ??
            nestedData?.fedexDriverRows ??
            responseData?.fedexDriverItems ??
            responseData?.fedexDriverRows ??
            []
    );
    const fallbackRows = mapRowsFromApi(
        nestedData?.rows ?? responseData?.rows ?? []
    );

    const sections = [];
    if (eligibleRows.length) {
        sections.push({
            sectionKey: "eligible",
            bucket: "eligible",
            sectionTitle: String(
                nestedData?.statusMessage ??
                    responseData?.statusMessage ??
                    nestedData?.sectionTitle ??
                    "Candidates are eligible"
            ).trim(),
            rows: eligibleRows,
            selectedIds: [],
        });
    }
    if (fedexRows.length) {
        sections.push({
            sectionKey: "fedexDriver",
            bucket: "fedexDriver",
            sectionTitle: String(
                nestedData?.fedexSectionTitle ??
                    responseData?.fedexSectionTitle ??
                    "FedEx driver candidates"
            ).trim(),
            rows: fedexRows,
            selectedIds: [],
        });
    }
    if (!sections.length && fallbackRows.length) {
        sections.push({
            sectionKey: "eligible",
            bucket: "eligible",
            sectionTitle: String(
                nestedData?.statusMessage ??
                    responseData?.statusMessage ??
                    "Candidates are eligible"
            ).trim(),
            rows: fallbackRows,
            selectedIds: [],
        });
    }

    return sections;
};

const extractFilterResponse = (response = {}) => {
    const responseData = response?.data ?? response ?? {};
    const nestedData =
        responseData?.data &&
        typeof responseData.data === "object" &&
        !Array.isArray(responseData.data)
            ? responseData.data
            : responseData;

    return {
        sections: buildSectionsFromResponse(nestedData, responseData),
        clientId: nestedData?.clientId ?? responseData?.clientId ?? null,
        mailSourceId: nestedData?.mailSourceId ?? responseData?.mailSourceId ?? "",
        reportType: nestedData?.reportType ?? responseData?.reportType ?? "",
        toDate: nestedData?.toDate ?? responseData?.toDate ?? "",
    };
};

const isClientReportFilterSuccessful = (response) =>
    response != null &&
    response?.success !== false &&
    String(response?.status ?? "").toLowerCase() !== "error";

const areSelectedIdsEqual = (left = [], right = []) => {
    if (left.length !== right.length) return false;
    return left.every((id, index) => id === right[index]);
};

const clientReportFilterSchema = Yup.object().shape({
    reportType: Yup.string().trim().required("Report type is required"),
    clientId: Yup.string().trim().required("Client is required"),
    dateTo: Yup.string().trim().required("Date is required"),
});

const FilterFieldFeedback = ({ message = "" }) => (
    <div className="filter-field-feedback-slot" aria-live="polite">
        {message ? (
            <FormFeedback type="invalid" className="d-block filter-field-feedback mb-0">
                {message}
            </FormFeedback>
        ) : null}
    </div>
);

FilterFieldFeedback.propTypes = {
    message: PropTypes.string,
};

const ClientReportSectionTable = memo(function ClientReportSectionTable({
    section,
    onSelectionChange,
    onRowFieldBlur,
    resolveRowId,
}) {
    const handleSectionSelectionChange = useCallback(
        (selectedIds) => onSelectionChange(section.sectionKey, selectedIds),
        [onSelectionChange, section.sectionKey]
    );

    const handleSectionNotesBlur = useCallback(
        (row, value, field) =>
            onRowFieldBlur(section.sectionKey, row, value, field),
        [onRowFieldBlur, section.sectionKey]
    );

    return (
        <CommonTable
            columns={TABLE_COLUMNS}
            data={section.rows}
            isActionRequired={false}
            enableRowSelection
            isCheckboxLeftSide
            pageName="ClientReport"
            resolveRowId={resolveRowId}
            onSelectionChange={handleSectionSelectionChange}
            onNotesBlur={handleSectionNotesBlur}
        />
    );
});

const ClientReport = () => {
    const [filterForm, setFilterForm] = useState({
        clientId: "",
        [getClientAsyncFilterLabelFieldKey()]: "",
        reportType: "",
        dateTo: "",
    });
    const [reportSections, setReportSections] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingOptions, setLoadingOptions] = useState(true);
    const [sendingAction, setSendingAction] = useState(false);
    const [mailSourceOptions, setMailSourceOptions] = useState([]);
    const [emailFrom, setEmailFrom] = useState(null);
    const [preferredMailSourceId, setPreferredMailSourceId] = useState("");
    const [reportTypeOptions, setReportTypeOptions] = useState([]);
    const [submitTypeOptions, setSubmitTypeOptions] = useState([]);
    const [submitType, setSubmitType] = useState(null);
    const [filterErrors, setFilterErrors] = useState({});

    const resolveRowCandidateClientId = useCallback(
        (row = {}) => String(row?.candidateClientId ?? row?.id ?? "").trim(),
        []
    );

    useEffect(() => {
        const loadOptions = async () => {
            setLoadingOptions(true);
            try {
                const [reportTypes, submitTypes, mailSourceResponse] = await Promise.all([
                    fetchClientReportReportTypeOptions(),
                    fetchClientReportSubmitActionOptions(),
                    fetchReportMailSourceOptions(),
                ]);

                setReportTypeOptions(reportTypes);
                setSubmitTypeOptions(submitTypes);
                setSubmitType(submitTypes[0] ?? null);

                if (mailSourceResponse == null || mailSourceResponse?.success === false) {
                    setMailSourceOptions([]);
                } else {
                    setMailSourceOptions(mapReportMailSourceOptions(mailSourceResponse));
                }
            } finally {
                setLoadingOptions(false);
            }
        };
        loadOptions();
    }, []);

    useEffect(() => {
        if (!mailSourceOptions.length) {
            setEmailFrom(null);
            return;
        }
        const preferredId = strOrNull(preferredMailSourceId);
        const matchedOption = preferredId
            ? mailSourceOptions.find((option) => String(option.value) === preferredId)
            : null;
        setEmailFrom(matchedOption ?? mailSourceOptions[0]);
    }, [mailSourceOptions, preferredMailSourceId]);

    const hasReportRows = useMemo(
        () => reportSections.some((section) => section?.rows?.length),
        [reportSections]
    );

    const clientInitialOption = useMemo(
        () =>
            buildClientAsyncFilterInitialOption(
                filterForm?.clientId,
                filterForm[getClientAsyncFilterLabelFieldKey()]
            ),
        [filterForm]
    );

    const buildFilterPayload = () => ({
        clientId: strOrNull(filterForm?.clientId),
        dateTo: normalizeMetadataFilterDateForApi(
            filterForm?.dateTo || dayjs().format("YYYY-MM-DD")
        ),
        reportType: strOrNull(filterForm?.reportType),
    });

    const getClientReportList = async () => {
        try {
            await clientReportFilterSchema.validate(filterForm, { abortEarly: false });
            setFilterErrors({});
        } catch (validationError) {
            const nextErrors = {};
            validationError?.inner?.forEach((error) => {
                nextErrors[error.path] = error.message;
            });
            setFilterErrors(nextErrors);
            return;
        }

        setLoading(true);
        try {
            const response = await submitClientReportFilterData(buildFilterPayload());
            const {
                sections,
                mailSourceId: responseMailSourceId,
                reportType: responseReportType,
                toDate: responseToDate,
            } = extractFilterResponse(response);

            if (!isClientReportFilterSuccessful(response)) {
                setReportSections([]);
                setPreferredMailSourceId("");
                if (response?.message) {
                    toast.error(response.message);
                }
                return;
            }

            setFilterForm((previous) => ({
                ...previous,
                ...(responseReportType != null && responseReportType !== ""
                    ? { reportType: String(responseReportType) }
                    : {}),
                ...(responseToDate
                    ? {
                          dateTo: dayjs(responseToDate).isValid()
                              ? dayjs(responseToDate).format("YYYY-MM-DD")
                              : String(responseToDate),
                      }
                    : {}),
            }));

            setPreferredMailSourceId(strOrNull(responseMailSourceId) || "");
            setReportSections(sections);
        } catch (error) {
            toast.error(error?.message || "Unable to load client report");
            setReportSections([]);
            setPreferredMailSourceId("");
        } finally {
            setLoading(false);
        }
    };

    const updateSectionRows = useCallback((sectionKey, updater) => {
        setReportSections((previousSections) =>
            previousSections.map((section) =>
                section.sectionKey === sectionKey
                    ? {
                          ...section,
                          rows:
                              typeof updater === "function"
                                  ? updater(section.rows)
                                  : updater,
                      }
                    : section
            )
        );
    }, []);

    const handleSelectionChange = useCallback((sectionKey, selectedIds = []) => {
        const normalized = Array.isArray(selectedIds)
            ? selectedIds.map((id) => String(id).trim()).filter(Boolean)
            : [];
        setReportSections((previousSections) => {
            let hasChange = false;
            const nextSections = previousSections.map((section) => {
                if (section.sectionKey !== sectionKey) return section;
                const current = section.selectedIds ?? [];
                if (areSelectedIdsEqual(current, normalized)) return section;
                hasChange = true;
                return { ...section, selectedIds: normalized };
            });
            return hasChange ? nextSections : previousSections;
        });
    }, []);

    const handleRowFieldBlur = useCallback(
        (sectionKey, row = {}, value = "", field = "notes") => {
            const rowId = resolveRowCandidateClientId(row);
            if (!rowId || !sectionKey) return;
            const fieldName = field === "fedex_id" ? "fedex_id" : "notes";
            updateSectionRows(sectionKey, (rows) =>
                rows.map((item) =>
                    resolveRowCandidateClientId(item) === rowId
                        ? { ...item, [fieldName]: String(value ?? "") }
                        : item
                )
            );
        },
        [resolveRowCandidateClientId, updateSectionRows]
    );

    const buildSendItems = (sections = [], bucket = "eligible") => {
        const sectionList = Array.isArray(sections) ? sections : [];
        const targetSections = sectionList.filter((section) => section?.bucket === bucket);
        const selectedIdSet = new Set(
            targetSections.flatMap((section) => section?.selectedIds ?? [])
        );

        return targetSections.flatMap((section) =>
            (Array.isArray(section?.rows) ? section.rows : []).map((row) => {
                const candidateClientId = resolveRowCandidateClientId(row);
                return {
                    candidateClientId,
                    isSelected: selectedIdSet.has(candidateClientId),
                    notes: String(row?.notes ?? ""),
                    fedexId: String(row?.fedex_id ?? row?.fedexId ?? ""),
                    readyToSendNotes: String(row?.readyToSendNotes ?? ""),
                };
            })
        );
    };

    const buildSendPayload = () => ({
        reportType: strOrNull(filterForm?.reportType),
        clientId: strOrNull(filterForm?.clientId),
        toDate: normalizeMetadataFilterDateForApi(
            filterForm?.dateTo || dayjs().format("YYYY-MM-DD")
        ),
        mailSourceId: strOrNull(emailFrom?.value),
        submitType: strOrNull(submitType?.value ?? submitType?.label ?? submitType),
        eligibleItems: buildSendItems(reportSections, "eligible"),
        fedexDriverItems: buildSendItems(reportSections, "fedexDriver"),
    });

    const handleSubmitAction = async () => {
        const clientId = strOrNull(filterForm?.clientId);
        const mailSourceId = strOrNull(emailFrom?.value);
        const resolvedSubmitType = strOrNull(
            submitType?.value ?? submitType?.label ?? submitType
        );

        // if (!clientId) {
        //     toast.error("Please select a client");
        //     return;
        // }
        if (!mailSourceId) {
            toast.error("Mail source is required");
            return;
        }
        if (!resolvedSubmitType) {
            toast.error("Submit type is required");
            return;
        }
        if (!hasReportRows) {
            toast.error("No candidates available to submit");
            return;
        }

        const hasSelectedRows = reportSections.some(
            (section) => (section?.selectedIds ?? []).length > 0
        );
        if (!hasSelectedRows) {
            toast.error("Please select at least one candidate");
            return;
        }

        setSendingAction(true);
        try {
            const response = await submitClientReportSendData(buildSendPayload());

            if (response == null || response?.success === false) {
                toast.error(response?.message || "Unable to submit action");
                return;
            }

            toast.success(response?.message || "Action submitted successfully");
            await getClientReportList();
        } catch (error) {
            toast.error(error?.message || "Unable to submit action");
        } finally {
            setSendingAction(false);
        }
    };

    const handleFilterFieldChange = (event) => {
        const { name, value } = event.target;
        setFilterForm((previous) => ({
            ...previous,
            [name]: value,
        }));
        if (filterErrors?.[name]) {
            setFilterErrors((previous) => ({
                ...previous,
                [name]: "",
            }));
        }
    };

    const handleClientChange = (option) => {
        setFilterForm((previous) =>
            applyClientAsyncFilterOptionToFormState(previous, option)
        );
        if (filterErrors?.clientId) {
            setFilterErrors((previous) => ({
                ...previous,
                clientId: "",
            }));
        }
    };

    const handleReportTypeChange = (option) => {
        setFilterForm((previous) => ({
            ...previous,
            reportType: option?.value || "",
        }));
        if (filterErrors?.reportType) {
            setFilterErrors((previous) => ({
                ...previous,
                reportType: "",
            }));
        }
    };

    const getSelectedOption = (options, value) =>
        (Array.isArray(options) ? options : []).find(
            (option) => String(option?.value) === String(value)
        ) || null;

    return (
        <React.Fragment>
            <div className="page-content mail-report-page client-report-page">
                <Container fluid>
                    <Card>
                        <CardBody>
                            <h5 className="fw-bold mb-3">Client Report</h5>
                            <hr />

                            <div className="upper-filter-bar manage-user-filter-strip">
                                <Row className="g-2 g-md-3 align-items-end">
                                    <Col xs="12" md="6" lg={3}>
                                        <span className="manage-user-filter-label">Report Type</span>
                                        <Select
                                            className={`select2-selection${
                                                filterErrors?.reportType ? " is-invalid" : ""
                                            }`}
                                            options={reportTypeOptions}
                                            value={getSelectedOption(
                                                reportTypeOptions,
                                                filterForm?.reportType
                                            )}
                                            onChange={handleReportTypeChange}
                                            placeholder="Select report type"
                                            isSearchable={false}
                                            isDisabled={loadingOptions || !reportTypeOptions.length}
                                            menuPortalTarget={
                                                typeof document !== "undefined"
                                                    ? document.body
                                                    : null
                                            }
                                            menuPosition="fixed"
                                            styles={selectMenuStyles}
                                        />
                                        <FilterFieldFeedback message={filterErrors?.reportType} />
                                    </Col>
                                    <Col xs="12" md="6" lg={3}>
                                        <span className="manage-user-filter-label">Client</span>
                                        <div
                                            className={
                                                filterErrors?.clientId ? "is-invalid" : undefined
                                            }
                                        >
                                            <AsyncClientReportClientSelect
                                                key={`client-report-client-${filterForm?.clientId || ""}`}
                                                value={filterForm?.clientId || ""}
                                                initialOption={clientInitialOption}
                                                onChange={handleClientChange}
                                                placeholder="Select Client"
                                                className="select2-selection w-100"
                                            />
                                        </div>
                                        <FilterFieldFeedback message={filterErrors?.clientId} />
                                    </Col>
                                    <Col xs="12" md="6" lg={3}>
                                        <span className="manage-user-filter-label">Date</span>
                                        <Input
                                            type="date"
                                            name="dateTo"
                                            className={`border rounded w-100 category-search-input${
                                                filterErrors?.dateTo ? " is-invalid" : ""
                                            }`}
                                            value={filterForm?.dateTo || ""}
                                            onChange={handleFilterFieldChange}
                                        />
                                        <FilterFieldFeedback message={filterErrors?.dateTo} />
                                    </Col>
                                    <Col xs="12" md="6" lg={2}>
                                        <span
                                            className="manage-user-filter-label"
                                            aria-hidden="true"
                                        >
                                            &nbsp;
                                        </span>
                                        <Button
                                            color="primary"
                                            className="w-100 manage-user-search-btn"
                                            type="button"
                                            disabled={loading}
                                            onClick={getClientReportList}
                                        >
                                            <i className="fa fa-search me-1" aria-hidden="true" />
                                            Search
                                        </Button>
                                        <FilterFieldFeedback />
                                    </Col>
                                </Row>
                            </div>

                            <div className="upper-filter-bar manage-user-filter-strip mail-report-toolbar">
                                <Row className="g-2 g-md-3 align-items-end">
                                    <Col xs="12" md="6" lg={6}>
                                        <span className="manage-user-filter-label">Mail</span>
                                        <Select
                                            className="select2-selection"
                                            options={mailSourceOptions}
                                            value={emailFrom}
                                            onChange={setEmailFrom}
                                            placeholder="Select mail source"
                                            isSearchable={false}
                                            isDisabled={!mailSourceOptions.length}
                                            menuPortalTarget={
                                                typeof document !== "undefined"
                                                    ? document.body
                                                    : null
                                            }
                                            menuPosition="fixed"
                                            styles={selectMenuStyles}
                                        />
                                    </Col>
                                    <Col xs="12" md="6" lg={6}>
                                        <span className="manage-user-filter-label">Submit Type</span>
                                        <Select
                                            className="select2-selection"
                                            options={submitTypeOptions}
                                            value={submitType}
                                            onChange={setSubmitType}
                                            placeholder="Select submit type"
                                            isSearchable={false}
                                            isDisabled={loadingOptions || !submitTypeOptions.length}
                                            menuPortalTarget={
                                                typeof document !== "undefined"
                                                    ? document.body
                                                    : null
                                            }
                                            menuPosition="fixed"
                                            styles={selectMenuStyles}
                                        />
                                    </Col>
                                </Row>
                            </div>

                            <Row className="align-items-center mb-3">
                                <Col className="text-end">
                                    <Button
                                        color="primary"
                                        type="button"
                                        disabled={sendingAction || loading || !hasReportRows}
                                        onClick={handleSubmitAction}
                                    >
                                        <i className="mdi mdi-email-outline me-1" aria-hidden="true" />
                                        {sendingAction ? "Submitting…" : "Submit Action"}
                                    </Button>
                                </Col>
                            </Row>

                            {loading ? (
                                <Loaders />
                            ) : (
                                reportSections.map((section) => (
                                    <div key={section.sectionKey} className="mb-4">
                                        {section.sectionTitle ? (
                                            <div
                                                className="mail-report-status-message"
                                                role="status"
                                            >
                                                {section.sectionTitle}
                                            </div>
                                        ) : null}
                                        <ClientReportSectionTable
                                            section={section}
                                            onSelectionChange={handleSelectionChange}
                                            onRowFieldBlur={handleRowFieldBlur}
                                            resolveRowId={resolveRowCandidateClientId}
                                        />
                                    </div>
                                ))
                            )}
                        </CardBody>
                    </Card>
                </Container>
            </div>
        </React.Fragment>
    );
};

ClientReport.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(ClientReport);
